import type { ILocation, IOccupancyLocalStorage, IRoom } from '@/composables/Interfaces/appInterfaces'
import { FilterPageUrl, GdsCookie, OccupancyLocalStorage } from '@/constants'
import type { IAccosBarDto } from '@/composables/Interfaces/apiInterfaces'
import { ref } from 'vue'
import { accosBarDataToGuestDataStoreJsonString, setCookie } from '@/composables/cookie'
import { broadcastChannelMessages, CULTURE } from '@/composables/helpers'
import { watchIgnorable } from '@vueuse/core'
import { broadcasterFilterStore } from './filter.store'

export const location = ref<ILocation | null>(null)
export const date = ref<(Date | null)[] | null>(null)
export const rooms = ref<Array<IRoom>>([{ adults: 2, children: 0, childrenAges: [] }])
export const pets = ref<{ hasPets: boolean; message: string }>({ hasPets: false, message: '' })

/**
 * variable which stores full area table in current language
 */
export const areas = ref<IAccosBarDto[] | null>([])

/**
 * ref value which stores every single data of accosbar
 *
 * Note: ref value changes if one of the values in the object change.
 */
export const accosBarData = ref({ location, date, rooms, pets })

export const popupVisible = ref(false)

export const broadcasterAccosBarStore = new BroadcastChannel(OccupancyLocalStorage)
export const messageReceiverAccosBarStore = new BroadcastChannel(OccupancyLocalStorage)

function emitToOtherApps() {
  broadcasterAccosBarStore.postMessage(broadcastChannelMessages.UPDATE)
}

messageReceiverAccosBarStore.addEventListener('message', (e) => {
  switch (e.data) {
    case broadcastChannelMessages.UPDATE:
      initAccosBarStoreFromLocalStorage()
      break
    default:
      console.warn(`NO HANDLER FOR MESSAGE: ${e.data}`)
  }
})

const watchAccosBarData = watchIgnorable(
  accosBarData.value,
  () => {
    setOccupancyLocalStorageAndCookie()
    emitToOtherApps()
  },
  { deep: true }
)

function setOccupancyLocalStorageAndCookie(): void {
  localStorage.setItem(OccupancyLocalStorage, JSON.stringify(accosBarData.value))
  setCookie(GdsCookie, accosBarDataToGuestDataStoreJsonString(), 365)
}

export function initAccosBarStoreFromLocalStorage(): void {
  const localStorageData = localStorage.getItem(OccupancyLocalStorage)
  if (localStorageData != null) {
    const storedData: IOccupancyLocalStorage = JSON.parse(localStorageData!)
    //ignoreUpdates otherwise endless loop if two apps are on the same page
    watchAccosBarData.ignoreUpdates(() => {
      rooms.value = storedData.rooms
      date.value = storedData.date ? storedData.date.map((d: string | null) => (d != null ? new Date(d) : null)) : null
      location.value = storedData.location
      pets.value = storedData.pets
    })
  }
}

export function redirectToFilterPage(): void {
  if (window.location.pathname !== FilterPageUrl) window.location.href = FilterPageUrl
  broadcasterFilterStore.postMessage(broadcastChannelMessages.ACCOSBARUPDATE)
}

export function getPredefinedSuggestionsOnFocus(): Array<{ areaId: number; name: string }> | null {
  // -- 4003 - dolomiten - 228
  // -- 4054 - meran und umgebung - 164
  // -- 4007 - kronplatz - 123
  // -- 4006 - hochpustertal - 22
  // -- 4110 - bozen und umgebung - 14
  // -- 4075 - brixen - 14
  // -- 4087 - sterzing - 12
  // -- 4067 - dorf tirol - 10
  const predefinedAreaIds = [4003, 4054, 4007, 4006, 4110, 4075, 4087, 4067]
  const filteredAreaIds = areas.value?.filter((x) => predefinedAreaIds.includes(x.areaId))
  return filteredAreaIds ? [...filteredAreaIds] : null
}
